import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from '../services/supabase'
import { adminApi } from '../services/adminApi'

interface AdminAuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  isSuperAdmin: boolean
  authError: string | null
  signIn: (email: string, password: string) => Promise<{ data: any; error: AuthError | Error | null }>
  signOut: () => Promise<{ error: AuthError | Error | null }>
  isAuthenticated: boolean
  getAccessToken: () => string | undefined
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined)

export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext)
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider')
  }
  return context
}

interface AdminAuthProviderProps {
  children: ReactNode
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)

  useEffect(() => {
    // Get initial session and verify superadmin status
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()

        if (error) {
          console.error('Failed to get session:', error)
          setAuthError(error.message)
          setLoading(false)
          return
        }

        if (session) {
          setSession(session)
          setUser(session.user)

          // Set loading to false immediately - let dashboard handle its own loading
          setLoading(false)

          // Verify superadmin status in background
          adminApi.getDashboardStats()
            .then(() => {
              setIsSuperAdmin(true)
              setAuthError(null)
            })
            .catch((error) => {
              console.error('Not a superadmin:', error)
              setIsSuperAdmin(false)
              setAuthError('Superadmin access required')
              // Sign out if not superadmin
              supabase.auth.signOut()
            })
        } else {
          setLoading(false)
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        setAuthError(error instanceof Error ? error.message : 'Unknown error')
        setLoading(false)
      }
    }

    initializeAuth()

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Admin auth state changed:', event)
        setSession(session)
        setUser(session?.user ?? null)

        if (session) {
          // Verify superadmin status in background
          adminApi.getDashboardStats()
            .then(() => {
              setIsSuperAdmin(true)
              setAuthError(null)
            })
            .catch(() => {
              setIsSuperAdmin(false)
              setAuthError('Superadmin access required')
            })
        } else {
          setIsSuperAdmin(false)
        }

        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true)
      setAuthError(null)

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error

      // Verify superadmin status
      try {
        await adminApi.getDashboardStats()
        setIsSuperAdmin(true)
        return { data, error: null }
      } catch (adminError) {
        // Not a superadmin, sign out
        await supabase.auth.signOut()
        throw new Error('Superadmin access required')
      }
    } catch (error) {
      console.error('Sign in error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Sign in failed'
      setAuthError(errorMessage)
      return { data: null, error: error as AuthError | Error }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      setIsSuperAdmin(false)
      return { error: null }
    } catch (error) {
      console.error('Sign out error:', error)
      return { error: error as AuthError | Error }
    } finally {
      setLoading(false)
    }
  }

  const value: AdminAuthContextType = {
    user,
    session,
    loading,
    isSuperAdmin,
    authError,
    signIn,
    signOut,
    isAuthenticated: !!user && !!session,
    getAccessToken: () => session?.access_token
  }

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  )
}
